import { useDialog } from '@/context/dialogContext'
import { useEffect, useState } from 'react'
import './menuInfoMgmt.css'
import { useRedis } from '@/hooks/useRedis'
import { useAppSelector } from '@/redux/hooks'
import { menuInfo, MenuInfoData, successMessages } from '../menuInfoMgmtItem'
import { useAxios } from '@/context/axiosContext'
import { useNavigate } from 'react-router-dom'
import commonUtils from '@/utils/commonUtils'
import { Button } from '@/components'

type DetailMenuInfoProps = {
  label?: string
  path?: string
  id?: number
  value?: string
  menuList?: MenuInfoData[]
  getMenuInfoList?: () => void
  onSaveSuccess?: () => void
}

interface SubmitData {
  cmpnyCd: string
  menuPath: string
  menuPathSn: number
  regId: string
  regDate: string
  updId: string
  updDate: string
  infoSj?: string
  infoCn?: string
  useYn: string
  delYn: string
  infoCnList?: string[] // infoCnList는 선택적 속성으로 추가
}

const DetailMenuInfoMgmt: React.FC<DetailMenuInfoProps> = ({
  label,
  path,
  id,
  value,
  menuList,
  getMenuInfoList,
  onSaveSuccess,
}) => {
  const axios = useAxios()
  const { showAlert, showConfirm } = useDialog()
  const { getComCodes } = useRedis()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)

  const [descriptions, setDescriptions] = useState([
    { id: 1, value: '' },
    { id: 2, value: '' },
    { id: 3, value: '' },
    { id: 4, value: '' },
  ])
  const [menuDetail, setMenuDetail] = useState<MenuInfoData[]>([])
  const [formData, setFormData] = useState<MenuInfoData>({
    cmpnyCd: '001',
    menuPath: '',
    menuPathSn: 0,
    code: '',
    regId: user?.cstId,
    regDate: '',
    updId: user?.cstId,
    updDate: '',
    infoSj: '',
    infoCn: '',
    useYn: 'Y',
    delYn: 'N',
    infoCnList: [], // 리스트로 처리할 infoCnList 추가
  })

  useEffect(() => {
    console.log('formData 변경됨', formData)
  }, [formData])

  useEffect(() => {
    const found = menuList?.find((item: any) => item.menuPath === path)
    if (found) {
      setFormData((prevFormData) => ({
        ...prevFormData, // 기존 formData를 그대로 복사하고
        menuPath: found.menuPath,
        menuPathSn: found.menuPathSn, // menuPathSn만 업데이트
      }))
    }
  }, [menuList, path])

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      infoCnList: descriptions.map((desc) => desc.value),
    }))
  }, [descriptions])

  const handleContents = (id: number, newValue: string) => {
    setDescriptions((prev) =>
      prev.map((desc) =>
        desc.id === id ? { ...desc, value: newValue } : desc,
      ),
    )

    // 새로운 값이 입력될 때마다 infoCnList를 갱신
    setFormData((prev) => ({
      ...prev,
      infoCnList: descriptions.map((desc) => desc.value),
    }))
  }

  useEffect(() => {
    if (formData.menuPath && formData.menuPathSn) {
      getMenuInfoDetail()
    }
  }, [formData.menuPath, formData.menuPathSn])

  const getMenuInfoDetail = async () => {
    try {
      const response = await axios.get(`/menu/getMenuInfoDetail`, {
        params: {
          menuPath: formData.menuPath,
          menuPathSn: formData.menuPathSn,
        },
      })
      const menuDetail = response.data.data.menuDetail
      setMenuDetail(menuDetail)

      // infoCnList 세팅
      setDescriptions(
        menuDetail.infoCnList?.map((value: string, index: number) => ({
          id: index + 1,
          value,
        })) || [],
      )

      setFormData((prevFormData) => ({
        ...prevFormData, // 기존 formData를 그대로 복사하고
        infoSj: menuDetail.infoSj,
        infoCnList: menuDetail.infoCnList,
      }))
    } catch (err) {
      console.error('Error fetching List:', err)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  //내용추가버튼
  const handleAddInput = () => {
    const newId =
      descriptions.length > 0 ? descriptions[descriptions.length - 1].id + 1 : 1

    if (descriptions.length >= 10) {
      showAlert({
        title: '최대 10개까지 추가할 수 있어요.',
      })
      return
    }
    setDescriptions([...descriptions, { id: newId, value: '' }])
  }

  //내용삭제버튼
  const handleRemoveInput = (id: number) => {
    setDescriptions(descriptions.filter((desc) => desc.id !== id))
  }

  const resetContents = () => {
    setFormData((prevData) => ({
      ...prevData, // 기존 값 유지
      cmpnyCd: '001',
      infoSj: '',
      infoCn: '',
      infoCnList: [], // 초기 상태
    }))

    // descriptions 배열 초기화
    setDescriptions((prevDescriptions) => {
      const resetDescriptions = prevDescriptions.map((desc) => ({
        ...desc,
        value: '', // 모든 value를 빈 문자열로 초기화
      }))
      return resetDescriptions
    })
  }

  // 저장(Submit) 핸들러
  const handleSubmit = async () => {
    // 빈 문자열을 필터링하여 새로운 배열 생성
    const filteredInfoCnList = formData.infoCnList
      ?.map((item) => item.trim())
      .filter((item) => item !== '')

    // 필수 필드 체크
    if (!formData.infoSj || formData.infoSj.trim() === '') {
      showAlert({
        type: 'error',
        title: '제목을 입력해주세요.',
      })
      return
    }

    // 밸리데이션: infoCnList에 내용이 없으면 알림 표시하고 종료
    if (!filteredInfoCnList || filteredInfoCnList.length === 0) {
      showAlert({
        type: 'error',
        title: '내용을 입력해주세요.',
      })
      return
    }
    // 이 부분에서 원하는 구분자로 값을 결합합니다 (예: 세미콜론, 파이프 등)
    const submitData: SubmitData = {
      cmpnyCd: formData.cmpnyCd,
      menuPath: formData.menuPath,
      menuPathSn: formData.menuPathSn,
      regId: formData.regId,
      regDate: formData.regDate,
      updId: formData.updId,
      updDate: formData.updDate,
      infoSj: formData.infoSj,
      infoCn: formData.infoCn,
      useYn: formData.useYn,
      delYn: formData.delYn,
    }
    // infoCnList가 비어있지 않으면 submitData에 추가
    if (filteredInfoCnList && filteredInfoCnList.length > 0) {
      submitData.infoCnList = filteredInfoCnList
    }

    try {
      const response = await axios.post(
        `/menu/insertMenuInfoDetail`,
        submitData,
      )

      if (response.status === 200 && !response.data.common.resultCode) {
        showAlert({
          type: 'success',
          title: successMessages.insert,
        })
      }

      getMenuInfoList?.()
      onSaveSuccess?.()
      // setTimeout(() => {
      //   navigate(0)
      // }, 100)
    } catch (err) {
      //TODO 에러처리필요
      showAlert({
        type: 'error',
        title: '저장 중 오류가 발생했어요.',
      })
      console.error('Error:', err)
    }
  }

  return (
    <div>
      <form>
        <div>
          <label htmlFor="infoSj" style={{ marginBottom: '7px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span style={{ color: '#d09d30', fontWeight: 'bold' }}>
                  {label}
                </span>
                {menuInfo.detailTitle}
              </div>
              <div>
                <button
                  type="button"
                  className="reset-btn"
                  onClick={resetContents}
                >
                  초기화
                </button>
              </div>
            </div>
          </label>
          <input
            type="text"
            id="infoSj"
            name="infoSj"
            value={formData.infoSj}
            maxLength={255}
            onChange={handleChange}
          />
          <label
            htmlFor="description"
            style={{ marginTop: '30px', marginBottom: '12px' }}
          >
            <span style={{ color: '#d09d30', fontWeight: 'bold' }}>
              {label}
            </span>
            {menuInfo.detailContents}
          </label>
          {descriptions.map((desc, index) => (
            <div key={desc.id} className="description-input-container">
              <input
                type="text"
                id="infoCn"
                name="infoCn"
                value={desc.value}
                onChange={(e) => handleContents(desc.id, e.target.value)}
                maxLength={255}
                placeholder="내용을 입력해주세요.(최대 255자까지 입력 가능)"
              />
              {index === descriptions.length - 1 ? (
                <button
                  type="button"
                  onClick={handleAddInput}
                  className="add-btn"
                >
                  <span style={{ fontSize: '20px' }}>＋</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveInput(desc.id)}
                  className="remove-btn"
                >
                  <span style={{ fontSize: '20px' }}>−</span>
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="form-footer">
          <div className="select-group">
            <div className="select-item">
              <label htmlFor="useYn">사용여부</label>
              <select
                id="useYn"
                name="useYn"
                value={formData.useYn}
                onChange={handleChange}
              >
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </select>
            </div>
            <div className="select-item">
              <label htmlFor="delYn">삭제여부</label>
              <select
                id="delYn"
                name="delYn"
                value={formData.delYn}
                onChange={handleChange}
              >
                {getComCodes('DEL_YN').map((item) => (
                  <option key={'delYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="button" className="submit-btn" onClick={handleSubmit}>
            저장
          </button>
        </div>
      </form>
    </div>
  )
}

export default DetailMenuInfoMgmt
